import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { distinctUntilChanged, filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { combineLatest, noop, Observable, Subject } from 'rxjs';
import { NgbModalRef, NgbOffcanvasRef } from '@ng-bootstrap/ng-bootstrap';
import { FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import {
  AssignmentCustomerResponse,
  BASE_PERMISSIONS,
  baseAssignmentsActions,
  baseAssignmentsSelectors,
  BaseBreakpoint,
  BaseCoreState,
  BaseFormatDecimalPipe,
  baseFormsActions,
  baseInquiryActions,
  BaseInquiryDocumentToUploadInterface,
  BaseInquiryDocumentUploadService,
  baseInquirySelectors,
  BaseInquiryStatus,
  BaseRolesService,
  baseSharedActions,
  BaseToastAlertClasses,
  BaseSimilarInquiriesService,
  BaseViewportService,
  CustomerDepartmentQualificationsListItemResponse,
  CustomerInquiryListItemResponse,
  DepartmentListItemResponse,
  EditInquiryFormInterface,
  INITIAL_TIME_SHIFTS_VALUE,
  InquiryCreateRequest,
  InquiryCreateRequestExtended,
  InquiryDate,
  InquiryResponse,
  PspBranchShortInfoResponse,
  ShiftPlanCalendarDateInterface,
  ShiftPlanCalendarRangeInterface,
  SimilarInquiryMappedInterface,
  UserItemFullNameType,
} from 'base';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseCustomerServiceProviderSelectService } from '../../../../shared/_services/base-customer-service-provider-select.service';
import { BaseCustomerSharedService } from '../../../../shared/_services/base-customer-shared.service';

@Component({
  selector: 'base-customer-shift-plan',
  templateUrl: './base-customer-shift-plan.component.html',
})
export class BaseCustomerShiftPlanComponent implements OnInit, OnDestroy {
  @ViewChild('sideBar', { static: true }) sidebarContent: ElementRef;
  @ViewChild('serviceProviderSelectModal', { static: true }) serviceProviderSelectModal: ElementRef;

  newInquiry: InquiryCreateRequest;
  offcanvas: NgbOffcanvasRef;
  isSidebarVisible = false;
  modalRef: NgbModalRef;
  isEditInquiryFormSubmitted = false;
  canPublishInquiry: boolean;
  editInquiryForm: FormGroup<EditInquiryFormInterface>;
  inquiryId: undefined | number;
  ranges: ShiftPlanCalendarRangeInterface[] = [];
  /** Owned here rather than in either child, since the md and desktop layouts each render their own. */
  similarInquiries: SimilarInquiryMappedInterface[] = [];
  inquiryVisibilityAndNotificationsModalRef: NgbModalRef;
  isEmployeeShiftPlan: boolean;
  isDirectInquiry: boolean;
  inquiredEmployeeName: string;
  employeeAssignmentId: number | undefined;
  assignmentData: AssignmentCustomerResponse;
  employeeBranches: PspBranchShortInfoResponse[] = [];
  documentToUpload: BaseInquiryDocumentToUploadInterface | null = null;
  isDocumentRemoved: boolean = false;
  isMdLayout$: Observable<boolean>;

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<BaseCoreState>,
    private rolesService: BaseRolesService,
    private fb: NonNullableFormBuilder,
    private formatDecimalPipe: BaseFormatDecimalPipe,
    private location: Location,
    private serviceProviderService: BaseCustomerServiceProviderSelectService,
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: BaseCustomerSharedService,
    private inquiryDocumentUploadService: BaseInquiryDocumentUploadService,
    private viewportService: BaseViewportService,
    private similarInquiriesService: BaseSimilarInquiriesService
  ) {}

  ngOnInit(): void {
    this.isMdLayout$ = this.viewportService.isBelow(BaseBreakpoint.md);

    this.canPublishInquiry = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PUBLISH_INQUIRY);

    this.isEmployeeShiftPlan = this.router.url.includes('employee-shift-plan');
    if (this.isEmployeeShiftPlan) {
      this.inquiredEmployeeName = this.route.snapshot.queryParamMap.get('inquiryFor') || '';
      const assignmentIdParam = this.route.snapshot.queryParamMap.get('assignmentId');
      this.employeeAssignmentId = assignmentIdParam !== null ? +assignmentIdParam : undefined;
      if (this.employeeAssignmentId) {
        this.store.dispatch(
          baseAssignmentsActions.loadAssignment({ payload: { assignment: this.employeeAssignmentId } })
        );

        this.store
          .select(baseAssignmentsSelectors.getAssignment)
          .pipe(
            filter((state) => !!state.ok),
            map((state) => state.data as AssignmentCustomerResponse),
            take(1)
          )
          .subscribe((assignmentData) => {
            this.assignmentData = assignmentData;
          });
      }
    }

    this.store
      .select(baseInquirySelectors.getShiftPlanRangeList)
      .pipe(
        takeUntil(this.destroy$),
        filter((data) => !!data)
      )
      .subscribe((list) => {
        this.ranges = list;
      });

    this.store
      .select(baseInquirySelectors.getCustomerInquiry)
      .pipe(
        filter((state) => !!state.ok),
        map((state) => state.data as InquiryResponse),
        take(1)
      )
      .subscribe((inquiryData) => {
        this.isDirectInquiry = !!inquiryData.directlyInquired;
        if (this.isDirectInquiry) {
          this.employeeBranches = inquiryData.directlyInquired?.profile.pspBranches || [];
        }
      });

    this.store
      .select(baseInquirySelectors.getShiftPlanFormData)
      .pipe(
        takeUntil(this.destroy$),
        filter((data) => !!data),
        distinctUntilChanged()
      )
      .subscribe((data) => {
        this.newInquiry = data;
        this.inquiryId = data.id as number;
      });

    this.initEditForms();
    this.subscribeToSimilarInquiries();
    this.subscribeToInquiryDocumentInfo();
  }

  ngOnDestroy() {
    this.store.dispatch(baseInquiryActions.resetShiftPlanData());
    this.store.dispatch(baseInquiryActions.resetInquiryDocument());
    this.destroy$.next();
    this.destroy$.complete();
  }

  collectInquiryDates(list: ShiftPlanCalendarRangeInterface[]): InquiryDate[] {
    return list.flatMap(({ dates, rangeData }) => {
      return dates
        .filter(({ isVisible }) => isVisible)
        .map(({ dateString }) => ({
          date: dateString,
          shiftZd: rangeData.shifts.zd,
          shiftNd: rangeData.shifts.nd,
          shiftSd: rangeData.shifts.sd,
          shiftFd: rangeData.shifts.fd,
          flexShift: rangeData.flexShift,
        }));
    });
  }

  goToPreviousPage() {
    this.store.dispatch(baseFormsActions.clearStaffRequestForm());
    this.location.back();
  }

  publish() {
    this.isEditInquiryFormSubmitted = true;
    if (!this.editInquiryForm.valid) {
      this.editInquiryFormInvalid();
      return;
    }

    if (this.inquiryId) {
      if (this.isDirectInquiry) {
        this.sharedService.publishInquiryDirectly(this.inquiryId, this.employeeBranches);
      } else {
        if (this.documentToUpload) {
          this.store.dispatch(
            baseInquiryActions.uploadInquiryDocumentFile({
              payload: {
                documentToUpload: this.documentToUpload,
                inquiryId: this.inquiryId,
                requestData: this.prepareOutput(),
                updateInquiryCallback: () => {
                  this.openInquiryVisibilityNotificationModal(this.inquiryId as number);
                },
              },
            })
          );
        } else {
          this.store.dispatch(
            baseInquiryActions.updateCustomerInquiry({
              payload: {
                inquiry: this.inquiryId,
                body: this.prepareOutput(),
                callback: () => {
                  this.openInquiryVisibilityNotificationModal(this.inquiryId as number);
                },
              },
            })
          );
        }
      }
    } else {
      const requestData = this.prepareOutput();
      const callback = (inquiryId: number) => {
        this.inquiryId = inquiryId;
        if (this.isEmployeeShiftPlan) {
          this.sharedService.publishInquiryDirectly(inquiryId, this.assignmentData.profile.pspBranches);
        } else {
          this.openInquiryVisibilityNotificationModal(inquiryId);
        }
      };

      if (this.documentToUpload) {
        this.store.dispatch(
          baseInquiryActions.uploadInquiryDocumentFile({
            payload: {
              documentToUpload: this.documentToUpload,
              requestData,
              createInquiryCallback: callback,
            },
          })
        );
      } else {
        this.store.dispatch(
          baseInquiryActions.createInquiry({
            payload: {
              data: requestData,
              callback,
            },
          })
        );
      }
    }
  }

  private subscribeToInquiryDocumentInfo(): void {
    combineLatest([
      this.store.select(baseInquirySelectors.getInquiryDocumentInfo),
      this.inquiryDocumentUploadService.documentToUpload$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([inquiryDocumentState, documentToUpload]) => {
        this.isDocumentRemoved = !documentToUpload && !inquiryDocumentState.data && !!inquiryDocumentState.ok;
        this.documentToUpload = documentToUpload;
      });
  }

  private openInquiryVisibilityNotificationModal(inquiryId: number) {
    this.inquiryVisibilityAndNotificationsModalRef =
      this.serviceProviderService.openInquiryVisibilityAndNotificationsModal(this.serviceProviderSelectModal);

    this.inquiryVisibilityAndNotificationsModalRef.result.then(() => {
      this.updateInquiryStatus(inquiryId);
    }, noop);
  }

  private updateInquiryStatus(inquiryId: number) {
    this.store.dispatch(
      baseInquiryActions.updateInquiryStatus({
        payload: {
          inquiry: inquiryId,
          status: BaseInquiryStatus.published,
          callback: () => {
            this.goToPreviousPage();
          },
        },
      })
    );
  }

  saveUnpublishedInquiry() {
    this.isEditInquiryFormSubmitted = true;
    if (!this.editInquiryForm.valid) {
      this.editInquiryFormInvalid();
      return;
    }

    const requestData = this.prepareOutput();
    const callback = () => {
      this.goToPreviousPage();
    };

    if (this.inquiryId) {
      if (this.documentToUpload) {
        this.store.dispatch(
          baseInquiryActions.uploadInquiryDocumentFile({
            payload: {
              documentToUpload: this.documentToUpload,
              requestData,
              inquiryId: this.inquiryId,
              updateInquiryCallback: callback,
            },
          })
        );
      } else {
        this.store.dispatch(
          baseInquiryActions.updateCustomerInquiry({
            payload: {
              inquiry: this.inquiryId,
              body: requestData,
              callback,
            },
          })
        );
      }
    } else {
      if (this.documentToUpload) {
        this.store.dispatch(
          baseInquiryActions.uploadInquiryDocumentFile({
            payload: {
              documentToUpload: this.documentToUpload,
              requestData,
              createInquiryCallback: callback,
            },
          })
        );
      } else {
        this.store.dispatch(
          baseInquiryActions.createInquiry({
            payload: {
              data: requestData,
              callback,
            },
          })
        );
      }
    }
  }

  toggleSidebar(): void {
    this.isSidebarVisible = !this.isSidebarVisible;
  }

  private editInquiryFormInvalid(): void {
    if (
      this.editInquiryForm.controls.jobDescription.errors?.required ||
      this.editInquiryForm.controls.mainQualification.errors?.required ||
      this.editInquiryForm.controls.responsible.errors?.required
    ) {
      this.store.dispatch(
        baseSharedActions.addSystemAlert({
          payload: {
            class: BaseToastAlertClasses.error,
            body: 'validation.required',
          },
        })
      );
      if (this.viewportService.isBelowNow(BaseBreakpoint.xxxl)) {
        this.toggleSidebar();
      }
    }
  }

  /**
   * Runs after initEditForms, since loading needs the raw form value — the range selector emits
   * synchronously on subscribe.
   */
  private subscribeToSimilarInquiries(): void {
    this.store
      .select(baseInquirySelectors.getSimilarInquiriesList)
      .pipe(
        filter((state) => !!state.ok),
        map((response) => response.data as CustomerInquiryListItemResponse[]),
        withLatestFrom(
          this.store
            .select(baseInquirySelectors.getShiftPlanList)
            .pipe(map((data) => data.list as ShiftPlanCalendarDateInterface[]))
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(([inquiries, calendarDates]) => {
        this.similarInquiries = this.similarInquiriesService.generateSimilarInquiries(inquiries, calendarDates);
      });

    this.store
      .select(baseInquirySelectors.getShiftPlanRangeList)
      .pipe(
        takeUntil(this.destroy$),
        filter((data) => !!data)
      )
      .subscribe((ranges) => {
        if (ranges.length === 0) {
          this.similarInquiries = [];
        }

        this.similarInquiriesService.loadSimilarInquiries(this.editInquiryForm.getRawValue(), ranges);
      });
  }

  private initEditForms(): void {
    this.editInquiryForm = this.fb.group({
      unlimited: [false],
      dates: [[] as string[]],
      jobDescription: ['', [Validators.required]],
      weeklyWorkingHours: this.fb.control(''),
      requirements: [[] as number[]],
      customerDepartment: [null as DepartmentListItemResponse | null],
      mainQualification: [null as CustomerDepartmentQualificationsListItemResponse | null, [Validators.required]],
      qualifications: [[] as number[]],
      responsible: [null as UserItemFullNameType | null, [Validators.required]],
      costUnit: [null as string | null, [Validators.maxLength(255)]],
      workEquipmentPsp: [null as string | null, [Validators.maxLength(255)]],
      workEquipmentCustomer: [null as string | null, [Validators.maxLength(255)]],
      remark: [null as string | null],
      document: [null as string | null],
      timeShiftsForm: this.fb.array(
        INITIAL_TIME_SHIFTS_VALUE.map((shift) =>
          this.fb.group({
            type: [shift.type],
            startTime: [shift.startTime, Validators.required],
            endTime: [shift.endTime, Validators.required],
            break: [shift.break],
          })
        )
      ),
    }) as FormGroup<EditInquiryFormInterface>;
  }

  private prepareOutput(): InquiryCreateRequestExtended {
    const editInquiryFormValue = this.editInquiryForm.getRawValue();

    return {
      unlimited: editInquiryFormValue.unlimited,
      dates: this.collectInquiryDates(this.ranges),
      shiftTimes: editInquiryFormValue.timeShiftsForm,
      mainQualification: editInquiryFormValue.mainQualification?.id as number,
      customerDepartment: editInquiryFormValue.customerDepartment?.id as number,
      responsible: editInquiryFormValue.responsible?.party as number,
      weeklyWorkingHours: this.formatDecimalPipe.transform(editInquiryFormValue.weeklyWorkingHours).replace(',', '.'),
      requirements: editInquiryFormValue.requirements || [],
      qualifications: editInquiryFormValue.qualifications || [],
      jobDescription: editInquiryFormValue.jobDescription,
      costUnit: editInquiryFormValue.costUnit || null,
      workEquipmentCustomer: editInquiryFormValue.workEquipmentCustomer || null,
      workEquipmentPsp: editInquiryFormValue.workEquipmentPsp || null,
      remark: editInquiryFormValue.remark || null,
      assignment: this.isEmployeeShiftPlan ? this.employeeAssignmentId : undefined,
      document: this.isDocumentRemoved ? undefined : editInquiryFormValue.document,
    };
  }
}
